import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Wallet, Calendar, Settings, LogOut, Search, Clock,
  CheckCircle, XCircle, RefreshCw, Crown, AlertTriangle, ArrowRight,
  Phone, DollarSign, Banknote, Star, Bell, MessageSquare, Sparkles,
  CreditCard, ArrowLeft, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  getHirerBookings, getProviderBookings, respondToBooking, completeBooking,
  switchRole, sendSOSAlert, requestWithdrawal, getWithdrawalHistory,
  createReview, getNotifications, markNotificationRead, markAllNotificationsRead,
  getUnreadCount, getInvoices, uploadMembershipScreenshot, createPaymentOrder
} from '../services/api';
import { SERVICES, getServiceById, MEMBERSHIP_PLANS } from '../data/services';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import UploadProfilePic from '../components/UploadProfilePic';
import UploadIdProof from '../components/UploadIdProof';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, refreshUser, token } = useAuth();

  // Tab state: 'hirer' or 'provider'
  const [activeTab, setActiveTab] = useState('hirer');
  const [showMembershipPopup, setShowMembershipPopup] = useState(false);

  const [hirerBookings, setHirerBookings] = useState([]);
  const [providerBookings, setProviderBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState({ open: false, bookingId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [sosModal, setSOSModal] = useState(false);
  const [sosMessage, setSOSMessage] = useState('');
  const [sosLoading, setSOSLoading] = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawUPI, setWithdrawUPI] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [reviewModal, setReviewModal] = useState({ open: false, booking: null });
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [reviewedBookings, setReviewedBookings] = useState(new Set());
  const [invoices, setInvoices] = useState([]);

  // Membership popup states
  const [selectedPlan, setSelectedPlan] = useState('5_years');
  const [showPayment, setShowPayment] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cashfree');
  const [membershipScreenshot, setMembershipScreenshot] = useState(null);
  const [membershipScreenshotPreview, setMembershipScreenshotPreview] = useState(null);
  const membershipFileRef = React.useRef(null);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    fetchAllData();
    // fetchAllData is defined below and stable for the life of the component;
    // we intentionally re-fetch only when user identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Handle payment callback (one-shot on mount — querystring is read once and
  // immediately stripped, so re-running on searchParams change would loop.)
  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status');
    const paymentType = searchParams.get('type');
    if (paymentStatus === 'success') {
      if (paymentType === 'membership') {
        toast.success('Membership payment successful! Your provider profile is now active.');
        refreshUser();
        setActiveTab('provider');
      } else {
        toast.success('Payment successful!');
      }
      searchParams.delete('payment_status');
      searchParams.delete('type');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    } else if (paymentStatus === 'failed') {
      const reason = searchParams.get('reason') || 'Payment failed';
      toast.error(`Payment failed: ${reason}`);
      searchParams.delete('payment_status');
      searchParams.delete('type');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [hirerRes, notifRes, unreadRes, invoiceRes] = await Promise.all([
        getHirerBookings().catch(() => ({ data: { bookings: [] } })),
        getNotifications().catch(() => ({ data: { notifications: [] } })),
        getUnreadCount().catch(() => ({ data: { count: 0 } })),
        getInvoices().catch(() => ({ data: { invoices: [] } }))
      ]);
      setHirerBookings(hirerRes.data.bookings || []);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(unreadRes.data.count || 0);
      setInvoices(invoiceRes.data.invoices || []);

      // If user has provider role, also fetch provider bookings
      if (user?.provider_active) {
        const provRes = await getProviderBookings().catch(() => ({ data: { bookings: [] } }));
        setProviderBookings(provRes.data.bookings || []);
        const wRes = await getWithdrawalHistory().catch(() => ({ data: { withdrawals: [] } }));
        setWithdrawals(wRes.data.withdrawals || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tab switching logic
  const handleTabSwitch = async (tab) => {
    if (tab === 'provider' && !user?.provider_active) {
      setShowMembershipPopup(true);
      return;
    }
    setActiveTab(tab);
    // Also update backend role for proper booking context
    if (tab === 'provider' && user?.current_role !== 'provider') {
      try { await switchRole(); await refreshUser(); } catch (e) { console.error('switchRole(provider) failed', e); }
    } else if (tab === 'hirer' && user?.current_role !== 'hirer') {
      try { await switchRole(); await refreshUser(); } catch (e) { console.error('switchRole(hirer) failed', e); }
    }
  };

  // Membership purchase via QR + Screenshot
  const handleMembershipFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large. Max 10MB.'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    setMembershipScreenshot(file);
    const reader = new FileReader();
    reader.onloadend = () => setMembershipScreenshotPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCashfreeMembership = async () => {
    setPaymentLoading(true);
    try {
      const res = await createPaymentOrder({ type: 'membership', plan_id: selectedPlan });
      if (res.data.payment_session_id) {
        const cashfree = window.Cashfree({ mode: res.data.cashfree_mode || 'production' });
        cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          redirectTarget: '_self'
        });
      } else if (res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment gateway error. Try QR code option.');
      setPaymentLoading(false);
    }
  };

  const handleQRMembership = async () => {
    if (!membershipScreenshot) { toast.error('Please upload payment screenshot'); return; }
    setPaymentLoading(true);
    try {
      const res = await uploadMembershipScreenshot(selectedPlan, membershipScreenshot);
      const conf = res.data.verification?.confidence;
      toast.success(`Payment verified${conf ? ` (${conf} confidence)` : ''}! Membership activated!`);
      setShowMembershipPopup(false);
      setShowPayment(false);
      setMembershipScreenshot(null);
      setMembershipScreenshotPreview(null);
      await refreshUser();
      setActiveTab('provider');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed.');
    } finally { setPaymentLoading(false); }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch { toast.error('Failed'); }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error('markNotificationRead failed', e); }
  };

  const handleSubmitReview = async () => {
    if (!reviewText.trim()) { toast.error('Please write a review'); return; }
    setReviewLoading(true);
    try {
      await createReview({ booking_id: reviewModal.booking.id, rating: reviewRating, review: reviewText });
      toast.success('Review submitted!');
      setReviewedBookings(prev => new Set([...prev, reviewModal.booking.id]));
      setReviewModal({ open: false, booking: null });
      setReviewRating(5); setReviewText('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally { setReviewLoading(false); }
  };

  const handleAcceptBooking = async (bookingId) => {
    try { await respondToBooking(bookingId, 'accepted'); toast.success('Booking accepted!'); fetchAllData(); } catch { toast.error('Failed'); }
  };

  const handleRejectBooking = async () => {
    if (!rejectReason.trim()) { toast.error('Please provide a reason'); return; }
    try { await respondToBooking(rejectModal.bookingId, 'rejected', rejectReason); toast.success('Booking rejected'); setRejectModal({ open: false, bookingId: null }); setRejectReason(''); fetchAllData(); } catch { toast.error('Failed'); }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      const response = await completeBooking(bookingId);
      toast.success(response.data.both_completed ? 'Service completed! Payout released.' : 'Confirmation recorded. Waiting for other party.');
      fetchAllData(); await refreshUser();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSOS = async () => {
    if (!sosMessage.trim()) { toast.error('Please describe your emergency'); return; }
    setSOSLoading(true);
    try { await sendSOSAlert({ message: sosMessage }); toast.success('Emergency alert sent!'); setSOSModal(false); setSOSMessage(''); } catch { toast.error('Failed. Call +91 98105 02313'); } finally { setSOSLoading(false); }
  };

  const handleWithdraw = async () => {
    const amount = parseInt(withdrawAmount);
    if (!amount || amount < 100) { toast.error('Min Rs.100'); return; }
    if (amount > (user?.pending_earnings || 0)) { toast.error('Insufficient balance'); return; }
    if (!withdrawUPI.trim()) { toast.error('Enter UPI ID'); return; }
    setWithdrawLoading(true);
    try { await requestWithdrawal({ amount, upi_id: withdrawUPI }); toast.success('Withdrawal request submitted!'); setWithdrawModal(false); setWithdrawAmount(''); setWithdrawUPI(''); await refreshUser(); fetchAllData(); } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); } finally { setWithdrawLoading(false); }
  };

  const getStatusBadge = (status) => {
    const styles = { pending: 'status-pending', accepted: 'status-accepted', rejected: 'status-rejected', completed: 'status-completed', provider_rejected: 'status-pending', prepaid: 'status-accepted' };
    const labels = { provider_rejected: 'Provider Declined', prepaid: 'Awaiting Provider' };
    return `badge-status ${styles[status] || styles.pending}`;
  };

  if (!user) return null;

  const isProviderTab = activeTab === 'provider';
  const currentBookings = isProviderTab ? providerBookings : hirerBookings;

  // ===================== RENDER =====================
  return (
    <div className="min-h-screen bg-[#FDFCF8] pb-24 md:pb-8">
      {/* Header */}
      <header className="fixed top-0 w-full z-40 glass-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="HumanOnRent.AI" className="w-10 h-10 rounded-xl" />
              <span className="font-bold text-xl bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent hidden sm:block">HumanOnRent.AI</span>
            </Link>
            
            <div className="flex items-center gap-3">
              <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors" data-testid="notifications-bell">
                <Bell className="w-5 h-5 text-gray-600" />
                {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>}
              </button>
              <Button variant="ghost" size="icon" onClick={() => navigate('/profile')} data-testid="profile-btn"><Settings className="w-5 h-5" /></Button>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn"><LogOut className="w-5 h-5" /></Button>
            </div>
          </div>
        </div>
      </header>

      {/* SOS Button */}
      <button onClick={() => setSOSModal(true)} className="sos-btn" style={{ zIndex: 35 }} data-testid="sos-btn">
        <AlertTriangle className="w-5 h-5" /><span className="hidden sm:inline">Emergency SOS</span>
      </button>

      {/* Notifications Overlay */}
      {showNotifications && <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />}
      {showNotifications && (
        <div className="fixed top-20 right-4 sm:right-8 w-96 max-w-[calc(100vw-2rem)] z-50 premium-card shadow-xl border border-gray-200 max-h-[70vh] overflow-hidden flex flex-col" data-testid="notifications-panel">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && <button onClick={handleMarkAllRead} className="text-sm text-orange-600 hover:text-orange-700 font-medium" data-testid="mark-all-read-btn">Mark all read</button>}
          </div>
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500"><Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p>No notifications yet</p></div>
            ) : notifications.slice(0, 20).map((notif) => (
              <div key={notif.id} onClick={() => !notif.read && handleMarkRead(notif.id)} className={`p-4 border-b border-gray-50 cursor-pointer transition-colors ${notif.read ? 'bg-white' : 'bg-orange-50/50'} hover:bg-gray-50`} data-testid={`notification-${notif.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${notif.read ? 'bg-gray-300' : 'bg-orange-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{notif.title}</p>
                    <p className="text-gray-600 text-sm mt-0.5">{notif.message}</p>
                    <p className="text-gray-400 text-xs mt-1">{new Date(notif.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-28">
        {/* Welcome + profile pic */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-medium text-gray-900 mb-1">Welcome back, {user.name}</h1>
            <p className="text-gray-500 text-sm">Manage your bookings and services</p>
          </div>
          <UploadProfilePic userId={user.id} onUploaded={refreshUser} />
        </div>

        {/* ID Proof banner */}
        {(!user.id_proof_path || user.id_proof_required) && (
          <div className="mb-6">
            <UploadIdProof
              uploaded={!!user.id_proof_path}
              required={!!user.id_proof_required}
              onUploaded={refreshUser}
            />
          </div>
        )}

        {/* ===== TWO MAIN TABS ===== */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-2xl mb-8" data-testid="dashboard-tabs">
          <button
            onClick={() => handleTabSwitch('hirer')}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-sm transition-all ${
              activeTab === 'hirer' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-rent-human"
          >
            <Search className="w-4 h-4" />
            Rent a Human
          </button>
          <button
            onClick={() => handleTabSwitch('provider')}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-sm transition-all ${
              activeTab === 'provider' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-rent-yourself"
          >
            <Wallet className="w-4 h-4" />
            Rent Yourself & Earn
            {!user.provider_active && <Crown className="w-3.5 h-3.5 text-amber-500" />}
          </button>
        </div>

        {/* ===== HIRER TAB CONTENT ===== */}
        {activeTab === 'hirer' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="stat-card"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center"><Calendar className="w-6 h-6 text-amber-600" /></div><div><p className="text-sm text-gray-500">My Bookings</p><p className="text-2xl font-number font-bold">{hirerBookings.length}</p></div></div></div>
              <div className="stat-card"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center"><CheckCircle className="w-6 h-6 text-emerald-600" /></div><div><p className="text-sm text-gray-500">Completed</p><p className="text-2xl font-number font-bold">{hirerBookings.filter(b => b.status === 'completed').length}</p></div></div></div>
              <div className="stat-card col-span-2">
                <div className="flex items-center justify-between">
                  <div><p className="text-sm text-gray-500">Find Verified Humans</p><p className="text-lg font-bold text-gray-900">Search by city, service, or pincode</p></div>
                  <Button onClick={() => navigate('/search')} className="btn-premium" data-testid="find-human-btn"><Search className="w-4 h-4 mr-2" />Find Provider</Button>
                </div>
              </div>
            </div>

            {/* Hirer Bookings */}
            <div className="premium-card">
              <div className="p-6 border-b border-gray-100"><h2 className="text-xl font-medium text-gray-900">My Bookings</h2></div>
              <div className="p-6">
                {loading ? <div className="flex items-center justify-center py-12"><div className="spinner" /></div> :
                  hirerBookings.length === 0 ? (
                    <div className="empty-state"><div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4"><Calendar className="w-8 h-8 text-gray-400" /></div><h3 className="text-lg font-medium text-gray-700">No bookings yet</h3><p className="text-gray-500 mt-1">Search for a provider to get started</p><Button onClick={() => navigate('/search')} className="btn-premium mt-4"><Search className="w-4 h-4 mr-2" />Find Provider</Button></div>
                  ) : (
                    <div className="space-y-4">
                      {hirerBookings.map((booking) => {
                        const service = getServiceById(booking.service_id);
                        const ServiceIcon = service?.icon;
                        return (
                          <div key={booking.id} className="profile-card" data-testid={`booking-${booking.id}`}>
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex items-start gap-4">
                                <div className={`w-14 h-14 ${service?.color || 'bg-gray-500'} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>{ServiceIcon && <ServiceIcon className="w-7 h-7" />}</div>
                                <div>
                                  <h4 className="font-medium text-gray-900 text-lg">{booking.service_name}</h4>
                                  <p className="text-gray-500 text-sm">Provider: {booking.provider_name}</p>
                                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{booking.date}</span>
                                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{booking.time}</span>
                                    <span className="font-semibold text-amber-600">{booking.hours} hrs | Rs.{booking.total_amount?.toLocaleString('en-IN') || booking.total_amount}{booking.gst_amount && <span className="text-xs text-gray-400 ml-1">(incl. GST)</span>}</span>
                                  </div>
                                  {booking.notes && <p className="text-sm text-gray-500 mt-2 italic">"{booking.notes}"</p>}
                                  {booking.rejection_reason && <p className="text-sm text-red-600 mt-2"><strong>Reason:</strong> {booking.rejection_reason}</p>}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-3">
                                <span className={getStatusBadge(booking.status)}>{booking.status === 'provider_rejected' ? 'Provider Declined' : booking.status === 'prepaid' ? 'Awaiting Provider' : booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}</span>
                                {booking.status === 'accepted' && (
                                  <div className="flex flex-col items-end gap-2">
                                    {(booking.hirer_completed || booking.provider_completed) && (
                                      <div className="text-xs text-gray-500 space-y-1">
                                        {booking.hirer_completed && <span className="flex items-center gap-1 text-green-600"><CheckCircle className="w-3 h-3" /> Hirer confirmed</span>}
                                        {booking.provider_completed && <span className="flex items-center gap-1 text-green-600"><CheckCircle className="w-3 h-3" /> Provider confirmed</span>}
                                      </div>
                                    )}
                                    {!booking.hirer_completed && <Button size="sm" onClick={() => handleCompleteBooking(booking.id)} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full" data-testid={`complete-booking-${booking.id}`}><CheckCircle className="w-4 h-4 mr-1" />Confirm Complete</Button>}
                                    {booking.hirer_completed && !booking.provider_completed && <span className="text-xs text-amber-600 font-medium">Waiting for provider to confirm</span>}
                                  </div>
                                )}
                                {booking.status === 'completed' && !reviewedBookings.has(booking.id) && (
                                  <Button size="sm" onClick={() => setReviewModal({ open: true, booking })} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid={`review-booking-${booking.id}`}><Star className="w-4 h-4 mr-1" />Write Review</Button>
                                )}
                                {booking.status === 'completed' && reviewedBookings.has(booking.id) && <span className="text-sm text-green-600 font-medium flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Reviewed</span>}
                                {booking.status === 'provider_rejected' && (
                                  <div className="text-center">
                                    <p className="text-sm text-amber-600 font-medium mb-2">{(booking.max_retries || 2) - (booking.rejections_count || 0)} selection{((booking.max_retries || 2) - (booking.rejections_count || 0)) !== 1 ? 's' : ''} remaining</p>
                                    <Button size="sm" onClick={() => navigate(`/search?rebookId=${booking.id}&service=${booking.service_id}`)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full" data-testid={`rebook-${booking.id}`}>
                                      <RefreshCw className="w-4 h-4 mr-1" />Select New Provider
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            </div>
          </>
        )}

        {/* ===== PROVIDER TAB CONTENT ===== */}
        {activeTab === 'provider' && user?.provider_active && (
          <>
            {/* Provider Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="stat-card"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center"><Calendar className="w-6 h-6 text-amber-600" /></div><div><p className="text-sm text-gray-500">Total Bookings</p><p className="text-2xl font-number font-bold">{providerBookings.length}</p></div></div></div>
              <div className="stat-card"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center"><Clock className="w-6 h-6 text-yellow-600" /></div><div><p className="text-sm text-gray-500">Pending</p><p className="text-2xl font-number font-bold">{providerBookings.filter(b => b.status === 'pending').length}</p></div></div></div>
              <div className="stat-card"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center"><Wallet className="w-6 h-6 text-emerald-600" /></div><div><p className="text-sm text-gray-500">Available</p><p className="text-2xl font-number font-bold text-emerald-600">Rs.{user.pending_earnings || 0}</p></div></div></div>
              <div className="stat-card"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center"><DollarSign className="w-6 h-6 text-blue-600" /></div><div><p className="text-sm text-gray-500">Total Earned</p><p className="text-2xl font-number font-bold">Rs.{user.total_earnings || 0}</p></div></div></div>
            </div>

            {/* Earnings Card */}
            <div className="earnings-card mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <p className="text-white/70 mb-1">Available for Withdrawal</p>
                  <p className="text-4xl font-number font-bold">Rs.{user.pending_earnings || 0}</p>
                  <p className="text-white/50 text-sm mt-1">Membership: {user.membership?.replace('_', ' ') || 'Active'}</p>
                </div>
                <Button onClick={() => setWithdrawModal(true)} disabled={(user.pending_earnings || 0) < 100} className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-6" data-testid="withdraw-btn">
                  <Banknote className="w-4 h-4 mr-2" />Withdraw Funds
                </Button>
              </div>
            </div>

            {/* Provider Bookings */}
            <div className="premium-card">
              <div className="p-6 border-b border-gray-100"><h2 className="text-xl font-medium text-gray-900">Booking Requests</h2></div>
              <div className="p-6">
                {loading ? <div className="flex items-center justify-center py-12"><div className="spinner" /></div> :
                  providerBookings.length === 0 ? (
                    <div className="empty-state"><div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4"><Calendar className="w-8 h-8 text-gray-400" /></div><h3 className="text-lg font-medium text-gray-700">No booking requests yet</h3><p className="text-gray-500 mt-1">Complete your profile to start receiving requests</p><Button onClick={() => navigate('/profile')} className="btn-premium mt-4"><Settings className="w-4 h-4 mr-2" />Edit Profile</Button></div>
                  ) : (
                    <div className="space-y-4">
                      {providerBookings.map((booking) => {
                        const service = getServiceById(booking.service_id);
                        const ServiceIcon = service?.icon;
                        return (
                          <div key={booking.id} className="profile-card" data-testid={`provider-booking-${booking.id}`}>
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex items-start gap-4">
                                <div className={`w-14 h-14 ${service?.color || 'bg-gray-500'} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>{ServiceIcon && <ServiceIcon className="w-7 h-7" />}</div>
                                <div>
                                  <h4 className="font-medium text-gray-900 text-lg">{booking.service_name}</h4>
                                  <p className="text-gray-500 text-sm">Hirer: {booking.hirer_name}</p>
                                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{booking.date}</span>
                                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{booking.time}</span>
                                    <span className="font-semibold text-amber-600">{booking.hours} hrs | Rs.{booking.total_amount?.toLocaleString('en-IN') || booking.total_amount}</span>
                                  </div>
                                  {booking.notes && <p className="text-sm text-gray-500 mt-2 italic">"{booking.notes}"</p>}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-3">
                                <span className={getStatusBadge(booking.status)}>{booking.status === 'provider_rejected' ? 'Provider Declined' : booking.status === 'prepaid' ? 'Awaiting Provider' : booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}</span>
                                {booking.status === 'pending' && (
                                  <div className="flex items-center gap-2">
                                    <Button size="sm" onClick={() => handleAcceptBooking(booking.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" data-testid={`accept-booking-${booking.id}`}><CheckCircle className="w-4 h-4 mr-1" />Accept</Button>
                                    <Button size="sm" variant="outline" onClick={() => setRejectModal({ open: true, bookingId: booking.id })} className="border-red-300 text-red-600 hover:bg-red-50 rounded-full" data-testid={`reject-booking-${booking.id}`}><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                                  </div>
                                )}
                                {booking.status === 'accepted' && (
                                  <div className="flex flex-col items-end gap-2">
                                    {(booking.hirer_completed || booking.provider_completed) && (
                                      <div className="text-xs text-gray-500 space-y-1">
                                        {booking.hirer_completed && <span className="flex items-center gap-1 text-green-600"><CheckCircle className="w-3 h-3" /> Hirer confirmed</span>}
                                        {booking.provider_completed && <span className="flex items-center gap-1 text-green-600"><CheckCircle className="w-3 h-3" /> Provider confirmed</span>}
                                      </div>
                                    )}
                                    {!booking.provider_completed && <Button size="sm" onClick={() => handleCompleteBooking(booking.id)} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full" data-testid={`complete-booking-${booking.id}`}><CheckCircle className="w-4 h-4 mr-1" />Confirm Complete</Button>}
                                    {booking.provider_completed && !booking.hirer_completed && <span className="text-xs text-amber-600 font-medium">Waiting for hirer to confirm</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            </div>

            {/* Withdrawal History */}
            {withdrawals.length > 0 && (
              <div className="premium-card mt-8">
                <div className="p-6 border-b border-gray-100"><h2 className="text-xl font-medium text-gray-900">Withdrawal History</h2></div>
                <div className="p-6">
                  <div className="space-y-3">
                    {withdrawals.slice(0, 5).map((w) => (
                      <div key={w.id} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                        <div><p className="font-medium text-gray-900">Rs.{w.amount}</p><p className="text-sm text-gray-500">{new Date(w.created_at).toLocaleDateString()}</p></div>
                        <span className={`badge-status ${w.status === 'completed' ? 'status-completed' : w.status === 'rejected' ? 'status-rejected' : 'status-pending'}`}>{w.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tax Invoices - shown in both tabs */}
        {invoices.length > 0 && (
          <div className="premium-card mt-8" data-testid="invoices-section">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-medium text-gray-900">Tax Invoices</h2>
              <p className="text-sm text-gray-500 mt-1">Set India Business Pvt Ltd</p>
            </div>
            <div className="divide-y divide-gray-50">
              {invoices.map((inv) => (
                <div key={inv.invoice_number || `${inv.type}-${inv.date}-${inv.amount}`} className="p-4 hover:bg-gray-50 transition-colors" data-testid={`invoice-${inv.invoice_number}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono text-sm font-medium text-gray-900">{inv.invoice_number}</p>
                      <p className="text-sm text-gray-500 mt-1">{inv.type === 'booking' ? `Booking: ${inv.service_name || 'Service'}` : `Membership: ${inv.description || 'Plan'}`}</p>
                      <p className="text-xs text-gray-400 mt-1">{new Date(inv.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">Rs.{inv.total_amount?.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-gray-500">Base: Rs.{inv.base_amount} | GST: Rs.{inv.total_gst}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Nav */}
      <div className="mobile-nav">
        <div className="flex items-center justify-around">
          <button onClick={() => handleTabSwitch('hirer')} className={`flex flex-col items-center gap-1 ${activeTab === 'hirer' ? 'text-amber-600' : 'text-gray-500'}`}><Search className="w-5 h-5" /><span className="text-xs font-medium">Rent Human</span></button>
          <button onClick={() => handleTabSwitch('provider')} className={`flex flex-col items-center gap-1 ${activeTab === 'provider' ? 'text-amber-600' : 'text-gray-500'}`}><Wallet className="w-5 h-5" /><span className="text-xs font-medium">Earn</span></button>
          <button onClick={() => navigate('/profile')} className="flex flex-col items-center gap-1 text-gray-500"><Settings className="w-5 h-5" /><span className="text-xs font-medium">Profile</span></button>
        </div>
      </div>

      {/* ===== MEMBERSHIP POPUP ===== */}
      <Dialog open={showMembershipPopup} onOpenChange={setShowMembershipPopup}>
        <DialogContent className="premium-card sm:max-w-xl max-h-[90vh] overflow-y-auto">
          {!showPayment ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl">
                  <Crown className="w-6 h-6 text-amber-500" />
                  Activate Provider Profile
                </DialogTitle>
              </DialogHeader>
              <p className="text-gray-500 text-sm mb-4">
                Choose a membership plan to start accepting bookings and earning money. You'll earn 80% of every booking fee.
              </p>

              {/* Benefits */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 mb-6 border border-orange-100">
                <h4 className="font-semibold text-gray-900 mb-2">Why become a provider?</h4>
                <div className="grid grid-cols-2 gap-2">
                  {["Earn up to Rs.1 Lac/month", "Set your own hourly rate", "Accept/reject bookings freely", "Instant payment settlement", "Flexible working hours", "AI-powered client matching"].map((b) => (
                    <div key={b} className="flex items-center gap-1.5 text-sm text-gray-700"><CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />{b}</div>
                  ))}
                </div>
              </div>

              {/* Plans */}
              <div className="space-y-3 mb-6">
                {Object.values(MEMBERSHIP_PLANS).map((p) => (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPlan(p.id)}
                    className={`cursor-pointer rounded-2xl p-4 transition-all border-2 ${
                      selectedPlan === p.id ? 'border-orange-400 bg-orange-50/50' : 'border-gray-100 hover:border-orange-200'
                    }`}
                    data-testid={`popup-plan-${p.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === p.id ? 'border-orange-500 bg-orange-500' : 'border-gray-300'}`}>
                          {selectedPlan === p.id && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900">{p.validity}</h4>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {p.features.map((f) => <span key={f} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{f}</span>)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">Rs.{p.price}</p>
                        <p className="text-xs text-gray-500">GST (18%): Rs.{Math.round(p.price * 0.18)}</p>
                        <p className="text-sm font-semibold text-amber-600">Total: Rs.{p.total}</p>
                        {p.popular && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full mt-1 inline-block">Best Value</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button onClick={() => setShowPayment(true)} className="btn-premium w-full text-base py-6" data-testid="popup-proceed-btn">
                Continue to Payment — Rs.{MEMBERSHIP_PLANS[selectedPlan]?.total}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-center text-xs text-gray-400 mt-2">Non-refundable. One-time payment.</p>
            </>
          ) : (
            /* QR Payment Screen inside popup */
            <>
              <button onClick={() => { setShowPayment(false); setMembershipScreenshot(null); setMembershipScreenshotPreview(null); setPaymentMethod('cashfree'); }} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"><ArrowLeft className="w-4 h-4" /> Back to Plans</button>
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold">Complete Payment</h2>
                <p className="text-gray-500 mt-1">{MEMBERSHIP_PLANS[selectedPlan]?.validity} Membership</p>
              </div>
              
              {/* Price Breakdown */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="flex justify-between mb-2 text-sm"><span className="text-gray-600">Plan Price</span><span className="font-medium">Rs.{MEMBERSHIP_PLANS[selectedPlan]?.price}</span></div>
                <div className="flex justify-between mb-2 text-sm"><span className="text-gray-600">GST (18%)</span><span className="font-medium">Rs.{Math.round(MEMBERSHIP_PLANS[selectedPlan]?.price * 0.18)}</span></div>
                <div className="border-t border-gray-200 pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-amber-600 text-xl">Rs.{MEMBERSHIP_PLANS[selectedPlan]?.total}</span>
                </div>
              </div>

              {/* Payment Method Toggle */}
              <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
                <button onClick={() => setPaymentMethod('cashfree')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all ${paymentMethod === 'cashfree' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
                  <CreditCard className="w-4 h-4" /> Pay Online
                </button>
                <button onClick={() => setPaymentMethod('qr')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all ${paymentMethod === 'qr' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
                  QR Code
                </button>
              </div>

              {paymentMethod === 'cashfree' ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                    <p className="text-blue-800 text-sm text-center">You'll be redirected to a secure payment page powered by Cashfree.</p>
                  </div>
                  <Button onClick={handleCashfreeMembership} disabled={paymentLoading} className="btn-premium w-full text-base py-6" data-testid="popup-pay-btn">
                    {paymentLoading ? <><div className="spinner" /> Redirecting...</> : <>Pay Rs.{MEMBERSHIP_PLANS[selectedPlan]?.total} Online <CreditCard className="w-5 h-5 ml-2" /></>}
                  </Button>
                </>
              ) : (
                <>
                  {/* QR Code */}
                  <div className="bg-gray-50 rounded-xl p-3 mb-3 text-center">
                    <p className="text-sm font-medium text-gray-700 mb-2">Scan QR code to pay via UPI</p>
                    <img src="/payment-qr.jpeg" alt="Payment QR" className="w-40 h-40 mx-auto rounded-lg object-contain border border-gray-200" data-testid="dashboard-payment-qr" />
                    <p className="text-xs text-gray-500 mt-2">Pay to: SET INDIA BUSINESS PVT LTD</p>
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
                      <p className="text-amber-800 font-bold">Pay Exactly: Rs.{MEMBERSHIP_PLANS[selectedPlan]?.total}</p>
                    </div>
                  </div>

                  {/* Screenshot Upload */}
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Upload Payment Screenshot *</p>
                {membershipScreenshotPreview ? (
                  <div className="relative mt-1">
                    <img src={membershipScreenshotPreview} alt="Screenshot" className="w-full h-36 object-contain rounded-xl border border-gray-200 bg-gray-50" />
                    <button onClick={() => { setMembershipScreenshot(null); setMembershipScreenshotPreview(null); }}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => membershipFileRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-amber-400 hover:bg-amber-50/50 transition-all cursor-pointer mt-1"
                    data-testid="dashboard-upload-screenshot">
                    <CreditCard className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-gray-600 font-medium text-sm">Upload screenshot</p>
                    <p className="text-xs text-gray-400">JPG, PNG (Max 10MB)</p>
                  </button>
                )}
                <input ref={membershipFileRef} type="file" accept="image/*" onChange={handleMembershipFileSelect} className="hidden" />
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
                <p className="text-green-800 text-sm text-center">AI will verify your payment amount & recipient before activating membership.</p>
              </div>

              <Button onClick={handleQRMembership} disabled={paymentLoading || !membershipScreenshot} className="btn-premium w-full text-base py-6" data-testid="popup-qr-pay-btn">
                {paymentLoading ? <><div className="spinner" /> Verifying...</> : <>Verify & Activate <CheckCircle className="w-5 h-5 ml-2" /></>}
              </Button>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={(open) => setRejectModal({ open, bookingId: null })}>
        <DialogContent className="premium-card sm:max-w-md"><DialogHeader><DialogTitle>Reject Booking</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label htmlFor="reject-reason">Reason for Rejection</Label><Textarea id="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Please provide a valid reason..." className="input-premium mt-2 py-3" rows={4} data-testid="reject-reason-input" /></div>
            <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setRejectModal({ open: false, bookingId: null })} className="rounded-full">Cancel</Button><Button onClick={handleRejectBooking} className="bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="confirm-reject-btn">Reject Booking</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SOS Modal */}
      <Dialog open={sosModal} onOpenChange={setSOSModal}>
        <DialogContent className="premium-card sm:max-w-md border-red-200"><DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Emergency SOS</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">If you're in an unsafe situation, describe what's happening.</p>
            <div><Label htmlFor="sos-message">Describe your emergency</Label><Textarea id="sos-message" value={sosMessage} onChange={(e) => setSOSMessage(e.target.value)} placeholder="What's happening? Where are you?" className="input-premium mt-2 py-3" rows={4} data-testid="sos-message-input" /></div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4"><p className="text-red-800 text-sm flex items-center gap-2"><Phone className="w-4 h-4" />Emergency: <strong>+91 98105 02313</strong></p></div>
            <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setSOSModal(false)} className="rounded-full">Cancel</Button><Button onClick={handleSOS} disabled={sosLoading} className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="send-sos-btn">{sosLoading ? <div className="spinner" /> : 'Send Emergency Alert'}</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw Modal */}
      <Dialog open={withdrawModal} onOpenChange={setWithdrawModal}>
        <DialogContent className="premium-card sm:max-w-md"><DialogHeader><DialogTitle>Withdraw Earnings</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-100 rounded-xl p-4 text-center"><p className="text-sm text-gray-500">Available Balance</p><p className="text-3xl font-number font-bold text-emerald-600">Rs.{user?.pending_earnings || 0}</p></div>
            <div><Label htmlFor="withdraw-amount">Amount to Withdraw</Label><Input id="withdraw-amount" type="number" min="100" max={user?.pending_earnings || 0} value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="Min Rs.100" className="input-premium mt-2" data-testid="withdraw-amount-input" /></div>
            <div><Label htmlFor="withdraw-upi">UPI ID</Label><Input id="withdraw-upi" type="text" value={withdrawUPI} onChange={(e) => setWithdrawUPI(e.target.value)} placeholder="yourname@upi" className="input-premium mt-2" data-testid="withdraw-upi-input" /></div>
            <p className="text-sm text-gray-500">Processed within 24-48 hours.</p>
            <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setWithdrawModal(false)} className="rounded-full">Cancel</Button><Button onClick={handleWithdraw} disabled={withdrawLoading} className="btn-premium" data-testid="confirm-withdraw-btn">{withdrawLoading ? <div className="spinner" /> : 'Request Withdrawal'}</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={reviewModal.open} onOpenChange={(open) => { if (!open) setReviewModal({ open: false, booking: null }); }}>
        <DialogContent className="premium-card sm:max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-orange-500" />Write a Review</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {reviewModal.booking && <div className="bg-gray-50 rounded-xl p-4"><p className="font-medium text-gray-900">{reviewModal.booking.service_name}</p><p className="text-sm text-gray-500">Provider: {reviewModal.booking.provider_name}</p></div>}
            <div><Label>Rating</Label><div className="flex gap-2 mt-2" data-testid="review-stars">{[1,2,3,4,5].map(star => <button key={star} onClick={() => setReviewRating(star)} className="transition-transform hover:scale-110" data-testid={`star-${star}`}><Star className={`w-8 h-8 ${star <= reviewRating ? 'fill-orange-400 text-orange-400' : 'text-gray-300'}`} /></button>)}</div></div>
            <div><Label htmlFor="review-text">Your Review</Label><Textarea id="review-text" value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience..." className="input-premium mt-2 py-3" rows={4} data-testid="review-text-input" /></div>
            <div className="flex justify-end gap-3"><Button variant="outline" onClick={() => setReviewModal({ open: false, booking: null })} className="rounded-full">Cancel</Button><Button onClick={handleSubmitReview} disabled={reviewLoading} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="submit-review-btn">{reviewLoading ? <div className="spinner" /> : 'Submit Review'}</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
